import { c as _c } from "react/compiler-runtime";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type ProductPromo } from "@/api";
import { Price } from "@/components/product/price";
import { PromoCountdown } from "./countdown";
import { promoDiscountLabel, promoPrice } from "./promo-price";

// Акция на странице товара (П3, fd-promo-10): цена со скидкой, название акции
// и таймер до конца. Источник — поле `activePromo` товара (пакет П2), а пока
// его нет — `GET /promos/product/:id`. По нулю таймера блок прячется и
// страница снова показывает обычную цену: акция кончилась.
//
// Цена здесь — для показа; корзину считает шлюз.

type ActivePromoField = {
  id: string;
  title: string;
  discountType: "percent" | "amount";
  value?: number;
  discountValue?: number;
  endsAt: string;
  price?: number;
  kind?: string;
};
export function useProductPromo(product) {
  const $ = _c(16);
  const fromProduct = (product?.activePromo ?? null) as ActivePromoField | null;
  const t0 = product?.id;
  let t1;
  if ($[0] !== t0) {
    t1 = ["promos", "product", t0];
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== product?.id) {
    t2 = () => api.promos.productPromo(product?.id ?? "");
    $[2] = product?.id;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = Boolean(product?.id) && !fromProduct;
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      refetchInterval: 60000,
      staleTime: 30000
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const query = useQuery(t4);
  if (fromProduct && Date.parse(fromProduct.endsAt) > Date.now()) {
    const t5 = fromProduct.discountValue ?? fromProduct.value ?? 0;
    let t6;
    if ($[8] !== fromProduct.discountType || $[9] !== fromProduct.endsAt || $[10] !== fromProduct.id || $[11] !== fromProduct.kind || $[12] !== fromProduct.price || $[13] !== fromProduct.title || $[14] !== t5) {
      t6 = {
        id: fromProduct.id,
        title: fromProduct.title,
        kind: fromProduct.kind,
        discountType: fromProduct.discountType,
        discountValue: t5,
        endsAt: fromProduct.endsAt,
        price: fromProduct.price
      };
      $[8] = fromProduct.discountType;
      $[9] = fromProduct.endsAt;
      $[10] = fromProduct.id;
      $[11] = fromProduct.kind;
      $[12] = fromProduct.price;
      $[13] = fromProduct.title;
      $[14] = t5;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    return t6;
  }
  const promo = query.data;
  return promo && Date.parse(promo.endsAt) > Date.now() ? promo : null;
}
export function ProductPromoPrice(t0) {
  const $ = _c(21);
  const {
    productId,
    promo,
    basePrice,
    oldPrice
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== basePrice || $[1] !== promo.discountType || $[2] !== promo.discountValue) {
    t1 = promoPrice(basePrice, promo.discountType, promo.discountValue);
    $[0] = basePrice;
    $[1] = promo.discountType;
    $[2] = promo.discountValue;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const price = t1;
  const t2 = Math.max(oldPrice ?? 0, basePrice);
  let t3;
  if ($[4] !== price || $[5] !== t2) {
    t3 = <Price value={price} oldValue={t2} size="lg" pill="dark" />;
    $[4] = price;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = promo.discountType === "amount" ? `${promo.title} (${promoDiscountLabel(promo.discountType, promo.discountValue)})` : promo.title;
  let t5;
  if ($[7] !== t4) {
    t5 = <span className="text-[14px] leading-5 text-[#2E2E2E]">{t4}</span>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== productId || $[10] !== queryClient) {
    t6 = () => {
      queryClient.setQueryData(["promos", "product", productId], null);
      queryClient.invalidateQueries({
        queryKey: ["promos", "product", productId]
      });
      queryClient.invalidateQueries({
        queryKey: ["products", "get", productId]
      });
    };
    $[9] = productId;
    $[10] = queryClient;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== promo.endsAt || $[13] !== t6) {
    t7 = <PromoCountdown endsAt={promo.endsAt} tone="soft" onExpire={t6} />;
    $[12] = promo.endsAt;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t5 || $[16] !== t7) {
    t8 = <div className="flex flex-wrap items-center gap-2">{t5}{t7}</div>;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t3 || $[19] !== t8) {
    t9 = <div className="flex flex-col gap-2" data-testid="product-promo">{t3}{t8}</div>;
    $[18] = t3;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
}